// ShipmentsPage — Spedizioni/DDT del modulo ristorazione collettiva (T-215).
// Il responsabile di produzione crea bozze DDT (offline-ok), aggiunge righe
// dai pasti del giorno (productions) o righe libere, e le EMETTE online via
// RPC issue_shipment (numerazione fiscale per tenant/kind/anno — online-only
// by design: la bozza resta salvata se manca rete). L'emissione genera
// l'evento append-only `shipment_dispatched` (hash chain). I documenti emessi
// sono immutabili: si annullano (status='void') con motivo, mai DELETE.

import { toUserMessage } from '../../lib/errors.js';
import {
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonFab,
  IonFabButton,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToggle,
  IonToolbar,
} from '@ionic/react';
import {
  add,
  addCircleOutline,
  closeOutline,
  documentTextOutline,
  paperPlaneOutline,
  restaurantOutline,
  trashOutline,
} from 'ionicons/icons';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '../../lib/adapters/share/index.js';
import {
  aggregateDestinationDishes,
  buildShipmentStockVoidCommand,
  collettivaDietLabel,
  defaultMealLot,
  formatShipmentNumber,
  menuSlotForDate,
  resolveCollettivaDish,
  shipmentStockVoidCommandIdFor,
  ShipmentDispatchedPayloadSchema,
  ShipmentLineSchema,
  StockCommandV1Schema,
  type Destination,
  type Shipment,
  type ShipmentLine,
  type ShipmentStatus,
  type StockCommandV1,
} from '@trackeat/shared';
import { bluecoopMenuDishes, hasBluecoopMenu } from '../../lib/collettiva/bluecoopMenu.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useZoneBusinesses } from '../../lib/auth/useZoneBusinesses.js';
import {
  useCollettivaEnabled,
  useEnqueueMaster,
  useEvents,
  useMasters,
  useStockProjection,
  useTransportDocumentsAccessGate,
  useWarehouseEnabled,
} from '../../lib/data/queries.js';
import { getSupabase, SUPABASE_URL } from '../../lib/supabase.js';
import { getSyncEngine } from '../../lib/sync/engine.js';
import { projectedCarryingCost } from '../../lib/stockCarryingCost.js';
import { findExactThenLegacyStockEvent } from '../../lib/stockEventBusinessScope.js';
import {
  shipmentLineReservesStockV1,
  shipmentLineStockEffectV1,
} from '../../lib/shipmentStockPolicy.js';
import {
  buildShipmentSupplyAllocations,
  clearShipmentSupplyAllocations,
  shipmentLinesMatchOrderSnapshot,
  type ShipmentOrderBackedLineV1,
  type ShipmentSupplyAllocationV1,
} from '../../lib/shipmentSupplyAllocations.js';
import {
  canonicalProductionLot,
  draftReservedQtyForStockLocator,
  resolveProductionQuantityLimit,
  selectProductionCandidates,
  validateStockLocatorTotals,
  type ProductionQuantityLimitError,
  type StockLocatorLine,
} from '../../lib/transport/productionCandidates.js';

// Vista minima delle productions necessaria qui (master condiviso; i campi
// portions/meal_service sono le colonne additive T-215, lp_code T-221).
interface ProductionRow {
  id: string;
  business_id?: string | null;
  output_name: string;
  output_qty: number | null;
  output_uom: string | null;
  produced_at: string;
  expires_at?: string | null;
  portions?: number | null;
  // string per includere 'colazione' (T-349) senza dipendere dall'enum shared.
  meal_service?: string | null;
  recipe_id?: string | null;
  // T-221: lotto reale stampato sull'etichetta. Le lavorazioni senza etichetta
  // usano il lotto stock deterministico PROD-<production_id>.
  lp_code?: string | null;
  // T-349: produzione materializzata dal piano (match ordini → produzioni).
  source_production_plan_item_id?: string | null;
  // T-380: produzione da pre-stampa serale (recipe-linked).
  prestampa?: boolean | null;
  archived_at?: string | null;
}

// T-349 Fase 2: richieste di fornitura (ordini) — vista minima per compilare
// il DDT della destinazione dagli ordini accettati ed evaderli all'emissione.
interface SupplyRequestRow {
  id: string;
  tenant_id: string;
  destination_id: string | null;
  requested_for_date: string;
  status: string;
  // T-447: la riga porta anche letto/dieta (`menu_label`) e il trittico del preposto
  // (`primo/secondo/contorno/frutta`), usati per espandere l'ordine nei PIATTI reali.
  lines?: {
    meal_service: string;
    description: string;
    qty: number;
    uom?: string | null;
    menu_label?: string | null;
    letto?: string | null;
    primo?: { label?: string | null } | null;
    secondo?: { label?: string | null } | null;
    contorno?: { label?: string | null } | null;
    frutta?: { label?: string | null } | null;
  }[] | null;
  version?: number;
  archived_at?: string | null;
}

// T-447: vista minima ricetta per il resolver condiviso (base/variante).
interface RecipeRow {
  id: string;
  name: string;
  variant_of_recipe_id?: string | null;
  diet_code?: string | null;
  archived_at?: string | null;
}

interface AggOrderLine {
  meal_service: string;
  description: string;
  qty: number;
  uom: string;
}

const ORDER_MEAL_LABEL: Record<string, string> = { colazione: 'Colazione', pranzo: 'Pranzo', cena: 'Cena' };

// T-349 Fase 3: dal cutoff in poi (compreso) il modello collettiva è plan-only —
// stessa costante della Scheda di produzione giornaliera (ProductionDayPage).
const COLLETTIVA_PLAN_ONLY_FROM = '2026-07-01';

// Confronto nomi tollerante (accenti/maiuscole/spazi) per dedup righe e match
// ordine ↔ produzione pianificata.
function normDish(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

// Toglie il suffisso servizio ("Piatto — Pranzo" → "Piatto") per confrontare le
// righe compilate dagli ordini con quelle aggiunte dal picker (nome nudo).
function stripMealSuffix(s: string): string {
  return s.replace(/\s*—\s*(colazione|pranzo|cena)\s*$/i, '');
}

// T-447: il DDT è ciò che è stato PRODOTTO e SPEDITO → può contenere SOLO piatti
// con riscontro nella produzione pianificata (dayProductions). Un piatto ordinato
// ma non prodotto NON entra nel documento (niente DDT fasulli). La compilazione
// dagli ordini è solo una comodità: filtra e avvisa dei piatti non prodotti.

// T-221: lookup ricetta → food "prodotto finito" (foods.recipe_id) per
// agganciare le righe pasto allo stock e scaricarle all'emissione.
interface FoodRow {
  id: string;
  name: string;
  default_uom?: string | null;
  recipe_id?: string | null;
  archived_at?: string | null;
}

interface BusinessRow {
  id: string;
  tenant_id?: string | null;
  archived_at?: string | null;
}

interface StockInRow {
  business_id?: string | null;
  payload: {
    business_id?: string | null;
    food_id: string;
    batch_lot?: string | null;
    expiration_date?: string | null;
    supplier_id?: string | null;
    document_id?: string | null;
    stock_unit_cost?: number | null;
    unit_cost?: number | null;
    source_currency?: string | null;
    currency?: string | null;
    void_compensation?: boolean;
  };
}

// Riga in editing (testo libero negli input numerici, conversione al salvataggio).
// food_id e notes non hanno UI di edit qui: transitano dalle righe esistenti
// (formFromShipment → buildLines) senza essere riscritti a null a ogni edit.
interface LineDraft {
  production_id: string | null;
  food_id: string | null;
  description: string;
  batch_lot: string;
  qty: string;
  uom: string;
  containers: string;
  notes: string | null;
}

interface RestaurantProductionStock {
  foodId: string | null;
  availableQty: number | null;
  stockAvailableQty: number | null;
  declaredQty: number | null;
  uom: string;
  lot: string;
  disabledReason: string | null;
}

interface DdtForm {
  destination_id: string;
  ref_date: string; // YYYY-MM-DD — giorno di riferimento per pasti e lotto default
  // T-447: servizio del DDT ('pranzo'|'cena'|'colazione'|'') — uno per documento,
  // scritto in intestazione; le righe NON portano più il suffisso "— Pranzo".
  meal_service: string;
  // Valorizzate soltanto da "Compila dagli ordini": un DDT libero non evade
  // implicitamente richieste solo perché coincide destinazione/data.
  supply_allocations_v1: ShipmentSupplyAllocationV1[];
  lines: LineDraft[];
  transport_cause: string;
  invoice_reference: string;
  carrier_name: string;
  vehicle_plate: string;
  temp_hot: string;
  temp_cold: string;
  vehicle_check_ok: boolean;
  vehicle_check_notes: string;
  notes: string;
}

const DEFAULT_TRANSPORT_CAUSE = 'Vendita/somministrazione pasti';

function isoLocalDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function localDayOf(iso: string): string {
  return isoLocalDay(new Date(iso));
}

function parseNum(raw: string): number | null {
  const t = raw.trim().replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function productionLimitErrorMessage(error: ProductionQuantityLimitError): string {
  switch (error) {
    case 'invalid_output_qty': return 'quantità prodotta non disponibile';
    case 'missing_output_uom': return 'unità di misura della produzione mancante';
    case 'missing_food_uom': return 'unità del prodotto finito non configurata';
    case 'production_food_uom_mismatch': return 'unità della produzione non coerente col prodotto finito';
    case 'missing_balance': return 'saldo del lotto non presente nel magazzino';
    case 'production_balance_uom_mismatch': return 'unità della produzione non coerente col saldo del lotto';
    case 'no_available_stock': return 'lotto esaurito o già impegnato';
  }
}

function emptyForm(): DdtForm {
  return {
    destination_id: '',
    ref_date: isoLocalDay(new Date()),
    meal_service: '',
    supply_allocations_v1: [],
    lines: [],
    transport_cause: DEFAULT_TRANSPORT_CAUSE,
    invoice_reference: '',
    carrier_name: '',
    vehicle_plate: '',
    temp_hot: '',
    temp_cold: '',
    vehicle_check_ok: true,
    vehicle_check_notes: '',
    notes: '',
  };
}

function formFromShipment(s: Shipment): DdtForm {
  // T-447: servizio dal campo; per i DDT storici (null) si deriva dal suffisso
  // "— Pranzo/Cena" delle righe (legacy) — quelli restano com'erano.
  const legacyMeal = s.lines
    .map((l) => l.description.match(/—\s*(colazione|pranzo|cena)\s*$/i)?.[1]?.toLowerCase())
    .find((m): m is string => !!m) ?? '';
  return {
    destination_id: s.destination_id ?? '',
    ref_date: s.service_date ?? localDayOf(s.created_at),
    meal_service: s.meal_service ?? legacyMeal,
    supply_allocations_v1: s.supply_allocations_v1 ?? [],
    lines: s.lines.map((l) => ({
      production_id: l.production_id ?? null,
      food_id: l.food_id ?? null,
      description: l.description,
      batch_lot: l.batch_lot,
      qty: String(l.qty),
      uom: l.uom,
      containers: l.containers ?? '',
      notes: l.notes ?? null,
    })),
    transport_cause: s.transport_cause || DEFAULT_TRANSPORT_CAUSE,
    invoice_reference: s.invoice_reference ?? '',
    carrier_name: s.carrier_name ?? '',
    vehicle_plate: s.vehicle_plate ?? '',
    temp_hot: s.temp_hot_c != null ? String(s.temp_hot_c) : '',
    temp_cold: s.temp_cold_c != null ? String(s.temp_cold_c) : '',
    vehicle_check_ok: s.vehicle_check_ok ?? true,
    vehicle_check_notes: s.vehicle_check_notes ?? '',
    notes: s.notes ?? '',
  };
}

/**
 * Attende che la coda di sync sia DRENATA (pendingCount === 0) prima
 * dell'emissione: la RPC issue_shipment legge la riga lato server, quindi la
 * bozza/le modifiche appena salvate devono essere già arrivate — altrimenti
 * shipment_not_found o numerazione di contenuto stantio. Un solo tick può
 * tornare PRIMA che il push in background sia completato (re-entrancy), per
 * questo cicliamo finché la coda è vuota o scade il tempo (T-226: prima il
 * primo "Emetti" falliva e serviva un secondo click).
 */
async function drainSyncQueue(maxMs = 12_000): Promise<boolean> {
  if (!navigator.onLine) return false;
  const engine = getSyncEngine();
  const deadline = Date.now() + maxMs;
  while (Date.now() < deadline) {
    if (engine.getState().pendingCount === 0) return true;
    try {
      await Promise.race([
        engine.tick(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('sync_timeout')), 8_000),
        ),
      ]);
    } catch {
      // tick fallito/timeout: riproviamo fino al deadline
    }
    if (engine.getState().pendingCount === 0) return true;
    await new Promise((r) => setTimeout(r, 250));
  }
  return engine.getState().pendingCount === 0;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== 'string') return reject(new Error('FileReader did not return a string'));
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

const cardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 14,
  padding: '14px 16px',
  marginBottom: 10,
  boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
};

// Header di sezione numerato del "form sezionato" (1 · DESTINAZIONE E DATA, …).
const sectionHeaderStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  color: '#475569',
  letterSpacing: '0.05em',
  margin: '18px 4px 8px',
};

// Card con stripe laterale di completamento (pattern LabelsPage step 2):
// ambra = manca un campo obbligatorio, verde = sezione/riga completa.
const stripedCardStyle: React.CSSProperties = {
  display: 'flex',
  background: '#fff',
  borderRadius: 14,
  marginBottom: 10,
  boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
  overflow: 'hidden',
};

function stripeStyle(complete: boolean): React.CSSProperties {
  return { width: 4, background: complete ? '#10b981' : '#f59e0b', flexShrink: 0, alignSelf: 'stretch' };
}

// Badge di stato documento per la lista bozze/emessi.
const STATUS_BADGE: Record<ShipmentStatus, { label: string; color: string; bg: string }> = {
  draft: { label: 'BOZZA', color: '#f59e0b', bg: '#fef3c7' },
  issued: { label: 'EMESSO', color: '#15803d', bg: '#dcfce7' },
  void: { label: 'ANNULLATO', color: '#dc2626', bg: '#fef2f2' },
};

function statusBadgeStyle(status: ShipmentStatus): React.CSSProperties {
  const b = STATUS_BADGE[status];
  return {
    fontSize: 11,
    fontWeight: 700,
    color: b.color,
    background: b.bg,
    borderRadius: 6,
    padding: '2px 8px',
    letterSpacing: '0.04em',
  };
}

type ShipmentPageFlow = 'collective' | 'restaurant';

function ShipmentsPageContent({ flow }: { flow: ShipmentPageFlow }): JSX.Element {
  const collettivaEnabled = useCollettivaEnabled();
  const transportAccess = useTransportDocumentsAccessGate();
  const collectiveFlow = flow === 'collective';
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  // T-403: zona della sede attiva (null = tenant-wide).
  const { zoneIds } = useZoneBusinesses();
  const { data: shipments = [] } = useMasters<Shipment>('shipments');
  const { data: destinations = [] } = useMasters<Destination>('destinations');
  const { data: productions = [] } = useMasters<ProductionRow>('productions');
  const { data: foods = [] } = useMasters<FoodRow>('foods');
  const {
    data: businesses = [],
    isLoading: businessesLoading,
    isError: businessesError,
  } = useMasters<BusinessRow>('businesses');
  const canonicalUomByFood = useMemo(
    () => Object.fromEntries(
      foods
        .filter((food) => !food.archived_at && food.default_uom?.trim())
        .map((food) => [food.id, food.default_uom!.trim()]),
    ),
    [foods],
  );
  const { data: stockProjection } = useStockProjection({ canonicalUomByFood });
  const { data: stockIn = [] } = useEvents('stock_in', null);
  // T-447: ricette (base+varianti) per il resolver condiviso dieta→piatto reale.
  const { data: recipes = [] } = useMasters<RecipeRow>('recipes');
  // T-349 Fase 2: ordini della destinazione per compilare/evadere il DDT.
  const { data: supplyRequests = [] } = useMasters<SupplyRequestRow>('supply_requests');
  // T-221: con magazzino ON l'emissione genera i movimenti stock (scarico
  // sede mittente + carico sede di arrivo se la destinazione è interna).
  const warehouseEnabled = useWarehouseEnabled();
  const enqueueMaster = useEnqueueMaster();

  // T-228: sede attiva = magazzino da cui si spedisce (il prodotto finito vive
  // nel magazzino della sede che l'ha prodotto, es. il centro di trasformazione).
  const activeBusinessId = profile?.active_business_id ?? null;
  const activeTenantBusinesses = useMemo(
    () => businesses.filter((business) =>
      !business.archived_at
      && (business.tenant_id == null || business.tenant_id === profile?.active_tenant_id)),
    [businesses, profile?.active_tenant_id],
  );
  const allowLegacyUnscopedBusiness = !businessesLoading
    && !businessesError
    && activeBusinessId !== null
    && activeTenantBusinesses.length === 1
    && activeTenantBusinesses[0]?.id === activeBusinessId;

  // T-425: rimosso il calcolo della giacenza del prodotto finito
  // (finishedStockBase/availableFinished) che serviva al blocco di emissione:
  // in collettiva i piatti finiti non sono a magazzino, quindi il DDT non si
  // gata più sulla giacenza. Vedi il commento nel flusso di emissione più sotto.

  const [segment, setSegment] = useState<'bozze' | 'emessi'>('bozze');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Shipment | null>(null);
  const [form, setForm] = useState<DdtForm>(emptyForm);
  const [mealPickerOpen, setMealPickerOpen] = useState(false);
  const [mealSelected, setMealSelected] = useState<Record<string, boolean>>({});
  const [productionScope, setProductionScope] = useState<'today' | 'history'>('today');
  const [productionSearch, setProductionSearch] = useState('');
  // T-447: servizio scelto per il "Compila dagli ordini". Pranzo e cena sono
  // ordini/spedizioni DISTINTI → si compila UN servizio per volta (mai un DDT
  // unico con tutti i pasti del giorno).
  const [prefillMeal, setPrefillMeal] = useState<string>('pranzo');
  // T-447: piatti ORDINATI ma NON prodotti (nessun riscontro in produzione) esclusi
  // dall'ultima compilazione → mostrati come avviso (niente DDT fasulli).
  const [excludedFromOrders, setExcludedFromOrders] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [issuingId, setIssuingId] = useState<string | null>(null);
  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);
  // Numeri assegnati in questa sessione: il master locale si aggiorna solo al
  // prossimo pull (il trigger server blocca i campi di emissione fuori RPC),
  // quindi teniamo {id → numero} per mostrare subito il documento tra gli emessi.
  const [issuedLocal, setIssuedLocal] = useState<Record<string, { doc_number: number; doc_year: number }>>({});
  const [voidedLocal, setVoidedLocal] = useState<Set<string>>(() => new Set());
  const [pdfPrompt, setPdfPrompt] = useState<Shipment | null>(null);
  const [voidTarget, setVoidTarget] = useState<Shipment | null>(null);
  // fix #5: guard sincrono anti doppio-annullo → no doppia compensazione magazzino.
  const voidBusy = useRef(false);
  const [deleteTarget, setDeleteTarget] = useState<Shipment | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const destById = useMemo(() => {
    const m = new Map<string, Destination>();
    for (const d of destinations) m.set(d.id, d);
    return m;
  }, [destinations]);

  // T-403: il picker propone solo le destinazioni della ZONA attiva (sedi con
  // la stessa città); zoneIds null = nessuna zona configurata → tutte. Le
  // lookup sugli spedizioni storiche (destById) restano sull'elenco completo.
  const activeDestinations = useMemo(
    () => destinations
      .filter((d) => (
        !d.archived_at
        && (collectiveFlow
          ? (!zoneIds || (d.business_id != null && zoneIds.includes(d.business_id)))
          : d.business_id == null)
      ))
      .sort((a, b) => a.name.localeCompare(b.name, 'it')),
    [collectiveFlow, destinations, zoneIds],
  );

  function effNumber(s: Shipment): number | null {
    return s.doc_number ?? issuedLocal[s.id]?.doc_number ?? null;
  }
  function effYear(s: Shipment): number | null {
    return s.doc_year ?? issuedLocal[s.id]?.doc_year ?? null;
  }
  function effStatus(s: Shipment): ShipmentStatus {
    if (voidedLocal.has(s.id)) return 'void';
    if (s.status === 'draft' && issuedLocal[s.id]) return 'issued';
    return s.status;
  }

  const ddts = useMemo(
    () => shipments.filter((s) => (
      s.kind === 'ddt'
      && (collectiveFlow ? s.document_scope !== 'restaurant' : s.document_scope === 'restaurant')
      && !s.archived_at
      && activeBusinessId != null
      && s.business_id === activeBusinessId
    )),
    [activeBusinessId, collectiveFlow, shipments],
  );

  const drafts = useMemo(
    () =>
      ddts
        .filter((s) => effStatus(s) === 'draft')
        .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? '')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ddts, issuedLocal, voidedLocal],
  );

  const issued = useMemo(
    () =>
      ddts
        .filter((s) => effStatus(s) !== 'draft')
        .sort((a, b) => {
          const dy = (effYear(b) ?? 0) - (effYear(a) ?? 0);
          if (dy !== 0) return dy;
          return (effNumber(b) ?? 0) - (effNumber(a) ?? 0);
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ddts, issuedLocal, voidedLocal],
  );

  // Pasti (productions) del giorno di riferimento scelto nel form.
  const dayProductions = useMemo(() => {
    const already = new Set(form.lines.map((l) => l.production_id).filter((id): id is string => !!id));
    return productions
      .filter((p) =>
        !p.archived_at
        && localDayOf(p.produced_at) === form.ref_date
        && !already.has(p.id)
        // T-228: solo i pasti PRODOTTI da questa sede. Il prodotto finito vive
        // nel magazzino della sede che l'ha prodotto: da "magazzino secco" non
        // si possono spedire pasti fatti nel "Locale trasformazione". Le
        // Una produzione storica senza business_id è attribuibile soltanto al
        // tenant che ha una singola sede attiva. In multi-sede non può
        // alimentare il DDT di una sede arbitraria.
        && (
          p.business_id === activeBusinessId
          || (allowLegacyUnscopedBusiness && p.business_id == null)
        )
        // T-349 Fase 3 / T-380: in collettiva i pasti spedibili sono quelli del
        // PIANO o pre-stampati (stessa regola della Scheda giornaliera); le sole
        // produzioni nate da etichetta restano fuori dal picker dal cutoff.
        && (!collectiveFlow
          || form.ref_date < COLLETTIVA_PLAN_ONLY_FROM
          || !!p.source_production_plan_item_id
          || !!p.prestampa),
      )
      .sort((a, b) => a.output_name.localeCompare(b.output_name, 'it'));
  }, [
    productions,
    form.ref_date,
    form.lines,
    activeBusinessId,
    allowLegacyUnscopedBusiness,
    collectiveFlow,
  ]);

  const restaurantCandidates = useMemo(() => {
    const byId = new Map(productions.map((production) => [production.id, production]));
    const selected = selectProductionCandidates({
      productions: productions.map((production) => ({
        id: production.id,
        business_id: production.business_id ?? null,
        output_name: production.output_name,
        output_qty: production.output_qty,
        output_uom: production.output_uom,
        produced_at: production.produced_at,
        expires_at: production.expires_at ?? null,
        recipe_id: production.recipe_id ?? null,
        lp_code: production.lp_code ?? null,
        archived_at: production.archived_at ?? null,
      })),
      activeBusinessId,
      // Il contratto server T-563 richiede una sede esplicita sulla
      // lavorazione. Le produzioni legacy senza business_id restano visibili
      // nei flussi storici Collettiva, ma non sono selezionabili nel modulo
      // ristorante.
      allowLegacyUnscopedBusiness: false,
      excludedProductionIds: new Set(
        form.lines.map((line) => line.production_id).filter((id): id is string => !!id),
      ),
      search: productionSearch,
      now: new Date(),
    });
    return {
      today: selected.today.flatMap((candidate) => {
        const row = byId.get(candidate.id);
        return row ? [row] : [];
      }),
      history: selected.history.flatMap((candidate) => {
        const row = byId.get(candidate.id);
        return row ? [row] : [];
      }),
    };
  }, [
    activeBusinessId,
    form.lines,
    productionSearch,
    productions,
  ]);

  const selectableProductions = collectiveFlow
    ? dayProductions
    : restaurantCandidates[productionScope];

  const finishedFoodsByRecipe = useMemo(() => {
    const map = new Map<string, FoodRow[]>();
    for (const food of foods) {
      if (food.archived_at || !food.recipe_id) continue;
      const current = map.get(food.recipe_id) ?? [];
      current.push(food);
      map.set(food.recipe_id, current);
    }
    return map;
  }, [foods]);

  const eligibleRestaurantProductionById = useMemo(() => {
    if (collectiveFlow) return new Map<string, ProductionRow>();
    const byId = new Map(productions.map((production) => [production.id, production]));
    const selected = selectProductionCandidates({
      productions: productions.map((production) => ({
        id: production.id,
        business_id: production.business_id ?? null,
        output_name: production.output_name,
        output_qty: production.output_qty,
        output_uom: production.output_uom,
        produced_at: production.produced_at,
        expires_at: production.expires_at ?? null,
        recipe_id: production.recipe_id ?? null,
        lp_code: production.lp_code ?? null,
        archived_at: production.archived_at ?? null,
      })),
      activeBusinessId,
      allowLegacyUnscopedBusiness: false,
      now: new Date(),
    });
    return new Map(
      [...selected.today, ...selected.history].flatMap((candidate) => {
        const row = byId.get(candidate.id);
        return row ? [[candidate.id, row] as const] : [];
      }),
    );
  }, [
    activeBusinessId,
    collectiveFlow,
    productions,
  ]);

  const restaurantProductionStockFor = useCallback((
    reservationDraft: Shipment | null,
    currentLines: readonly StockLocatorLine[],
  ): Map<string, RestaurantProductionStock> => {
    const canReopenReservation = !!reservationDraft
      && reservationDraft.status === 'draft'
      && !reservationDraft.archived_at
      && reservationDraft.tenant_id === profile?.active_tenant_id
      && reservationDraft.business_id === activeBusinessId;
    const reservationLines = canReopenReservation ? reservationDraft.lines : [];
    const result = new Map<string, RestaurantProductionStock>();

    for (const production of eligibleRestaurantProductionById.values()) {
      const lot = canonicalProductionLot({
        id: production.id,
        lp_code: production.lp_code ?? null,
      });
      const linkedFoods = production.recipe_id
        ? finishedFoodsByRecipe.get(production.recipe_id) ?? []
        : [];
      const food = linkedFoods.length === 1 ? linkedFoods[0] : null;
      const balance = food
        ? stockProjection.balances.find((candidate) => (
            candidate.foodId === food.id
            && (candidate.batchLot ?? '') === lot
            && !candidate.closed
          )) ?? null
        : null;
      const reopenedAvailableQty = balance && food
        ? balance.availableQty + draftReservedQtyForStockLocator(reservationLines, {
            foodId: food.id,
            batchLot: lot,
            uom: balance.uom,
          })
        : null;
      const alreadyInForm = food && balance
        ? currentLines.reduce((total, line) => {
            if (
              line.production_id === production.id
              || line.food_id !== food.id
              || line.batch_lot?.trim() !== lot
              || line.uom?.trim().toLowerCase() !== balance.uom.trim().toLowerCase()
            ) return total;
            const qty = Number(line.qty);
            return Number.isFinite(qty) && qty > 0 ? total + qty : total;
          }, 0)
        : 0;
      const candidateBalanceQty = reopenedAvailableQty == null
        ? null
        : Math.max(0, reopenedAvailableQty - alreadyInForm);
      const limit = resolveProductionQuantityLimit({
        outputQty: production.output_qty,
        outputUom: production.output_uom,
        warehouseEnabled,
        foodUom: food?.default_uom ?? null,
        balanceQty: candidateBalanceQty,
        balanceUom: balance?.uom ?? null,
      });
      let disabledReason = limit.error
        ? productionLimitErrorMessage(limit.error)
        : null;
      if (warehouseEnabled) {
        if (!production.recipe_id) {
          disabledReason = 'lavorazione senza ricetta collegata';
        } else if (linkedFoods.length === 0) {
          disabledReason = 'prodotto finito non collegato alla ricetta';
        } else if (linkedFoods.length > 1) {
          disabledReason = 'più prodotti finiti collegati alla stessa ricetta';
        }
      }

      result.set(production.id, {
        foodId: warehouseEnabled ? (food?.id ?? null) : null,
        availableQty: limit.maxQty,
        stockAvailableQty: warehouseEnabled ? reopenedAvailableQty : null,
        declaredQty: limit.declaredQty,
        uom: limit.uom,
        lot,
        disabledReason,
      });
    }
    return result;
  }, [
    activeBusinessId,
    eligibleRestaurantProductionById,
    finishedFoodsByRecipe,
    profile?.active_tenant_id,
    stockProjection.balances,
    warehouseEnabled,
  ]);

  const productionStockById = useMemo(
    () => restaurantProductionStockFor(editing, form.lines),
    [editing, form.lines, restaurantProductionStockFor],
  );

  // SOLO la sede attiva del profilo — nessun fallback silenzioso alla prima
  // sede del tenant: il DDT finirebbe intestato a una sede arbitraria.
  // Allineato a TransferPage/ProductionDayPage (review).
  function resolveBusinessId(): string | null {
    return profile?.active_business_id ?? null;
  }

  function openNew(): void {
    setEditing(null);
    setForm({
      ...emptyForm(),
      transport_cause: collectiveFlow
        ? DEFAULT_TRANSPORT_CAUSE
        : 'Vendita prodotti alimentari',
    });
    setExcludedFromOrders([]);
    setEditorOpen(true);
  }

  function openEdit(s: Shipment): void {
    if (effStatus(s) !== 'draft') return;
    setEditing(s);
    setForm(formFromShipment(s));
    setExcludedFromOrders([]);
    setEditorOpen(true);
  }

  function updateLine(idx: number, patch: Partial<LineDraft>): void {
    setForm((f) => ({
      ...clearShipmentSupplyAllocations(f),
      lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    }));
  }

  function removeLine(idx: number): void {
    setForm((f) => ({
      ...clearShipmentSupplyAllocations(f),
      lines: f.lines.filter((_, i) => i !== idx),
    }));
  }

  // T-349 Fase 2: ordini ACCETTATI della destinazione scelta per il giorno di
  // riferimento, aggregati per (servizio, piatto). Sono la fonte con cui si
  // compila il DDT e ciò che l'emissione evade (status → 'fulfilled').
  const destOrders = useMemo(() => {
    if (!collectiveFlow || !form.destination_id) {
      return { requests: [] as SupplyRequestRow[], lines: [] as AggOrderLine[] };
    }
    const requests = supplyRequests.filter((r) =>
      !r.archived_at
      && r.destination_id === form.destination_id
      && r.requested_for_date === form.ref_date
      && (r.status === 'accepted' || r.status === 'in_production'));
    const m = new Map<string, AggOrderLine>();
    for (const r of requests) {
      for (const ln of r.lines ?? []) {
        const qty = Number(ln.qty) || 0;
        if (qty <= 0 || !ln.description) continue;
        const uom = (ln.uom && ln.uom.trim()) || 'pasti';
        const key = `${ln.meal_service}|${normDish(ln.description)}|${uom}`;
        const cur = m.get(key);
        if (cur) cur.qty += qty;
        else m.set(key, { meal_service: ln.meal_service, description: ln.description, qty, uom });
      }
    }
    const lines = [...m.values()].sort((a, b) =>
      a.meal_service.localeCompare(b.meal_service) || a.description.localeCompare(b.description, 'it'));
    return { requests, lines };
  }, [collectiveFlow, supplyRequests, form.destination_id, form.ref_date]);

  // T-447: ordini della destinazione RIPARTITI per servizio (pranzo/cena/colazione):
  // pasti + n. righe per servizio. Serve a compilare UN servizio per volta.
  const ordersByMeal = useMemo(() => {
    const m = new Map<string, { pasti: number; righe: number }>();
    for (const r of destOrders.requests) {
      for (const l of r.lines ?? []) {
        const qty = Number(l.qty) || 0;
        if (qty <= 0) continue;
        const cur = m.get(l.meal_service) ?? { pasti: 0, righe: 0 };
        cur.pasti += qty;
        cur.righe += 1;
        m.set(l.meal_service, cur);
      }
    }
    return m;
  }, [destOrders]);
  const availableMeals = useMemo(
    () => (['colazione', 'pranzo', 'cena'] as const).filter((mm) => ordersByMeal.has(mm)),
    [ordersByMeal],
  );
  // Servizio effettivo: la scelta dell'utente se ha ordini, altrimenti il primo disponibile.
  const effPrefillMeal = availableMeals.includes(prefillMeal as (typeof availableMeals)[number])
    ? prefillMeal
    : (availableMeals[0] ?? 'pranzo');

  // T-447: ricette (base+varianti) nel formato del resolver condiviso.
  const resolverRecipes = useMemo(
    () => recipes
      .filter((r) => !r.archived_at)
      .map((r) => ({ id: r.id, name: r.name, variant_of_recipe_id: r.variant_of_recipe_id ?? null, diet_code: r.diet_code ?? null })),
    [recipes],
  );
  // T-447: rotazione del giorno per categoria e SERVIZIO (pranzo/cena hanno piatti
  // diversi) — stessa fonte della Pianifica. Usata sia dal compila-ordini sia dalla
  // mappa dish_recipes passata a export-ddt per il dettaglio-reparto.
  const rotationForMeal = useCallback((meal: string, dateYmd: string): Record<string, string> => {
    const out: Record<string, string> = {};
    if (!hasBluecoopMenu(meal)) return out;
    const slot = menuSlotForDate(dateYmd);
    for (const d of bluecoopMenuDishes(slot, slot.weekday, meal === 'cena' ? 'cena' : 'pranzo')) out[d.category] = d.name;
    return out;
  }, []);

  // T-447: mappa label(catalogo) → nome RICETTA assegnata, per far scrivere a
  // export-ddt (dettaglio-reparto) il piatto reale invece della label astratta
  // ("1° Ordinario" → "Pasta al pomodoro"). Stesso pattern di export-piatti-zona
  // (T-445): la risoluzione delle varianti vive nel client (resolver condiviso).
  const buildDishRecipes = useCallback((s: Shipment): Record<string, string> => {
    const svcDate = localDayOf(s.issued_at ?? s.created_at);
    const rawLines = supplyRequests
      .filter((r) => !r.archived_at && r.destination_id === s.destination_id && r.requested_for_date === svcDate && r.status !== 'rejected')
      .flatMap((r) => r.lines ?? []);
    const map: Record<string, string> = {};
    for (const l of rawLines) {
      const rotation = rotationForMeal(l.meal_service, svcDate);
      for (const label of [l.primo?.label, l.secondo?.label, l.contorno?.label, l.frutta?.label]) {
        if (!label || map[label]) continue;
        const r = resolveCollettivaDish(label, { recipes: resolverRecipes, rotationByCategory: rotation, dietLabel: collettivaDietLabel });
        if (r) map[label] = r.recipe_name;
      }
    }
    return map;
  }, [supplyRequests, resolverRecipes, rotationForMeal]);

  // T-447: Compila il DDT dagli ordini accettati ESPANDENDO le diete nei PIATTI
  // reali (una riga per piatto), leggendo la produzione pianificata del giorno →
  // NOME RICETTA + lotto LP reale + QR (trace_token). Niente più etichetta-dieta
  // ("primo ordinario") sul documento. Le label si risolvono col resolver UNICO
  // condiviso (stessa logica della Pianifica), così il nome ricetta coincide con
  // `output_name` della produzione → match affidabile (porta lotto + token QR).
  // Append-only: non tocca le righe già presenti (dedup simmetrico per nome).
  function prefillFromOrders(): void {
    // UN servizio per volta (pranzo/cena/colazione sono spedizioni distinte).
    const meal = effPrefillMeal;
    const rawLines = destOrders.requests.flatMap((r) => r.lines ?? []).filter((l) => l.meal_service === meal);
    if (rawLines.length === 0) return;
    const dishes = aggregateDestinationDishes(rawLines, {
      recipes: resolverRecipes,
      rotationByCategory: rotationForMeal(meal, form.ref_date),
      dietLabel: collettivaDietLabel,
      catalog: [],
    });
    const fallbackLot = defaultMealLot(new Date(`${form.ref_date}T12:00:00`));
    // Righe PULITE (solo nome piatto, niente "— Servizio"): il servizio va
    // nell'INTESTAZIONE (form.meal_service). Dedup per nome piatto.
    const existing = new Set(form.lines.map((l) => normDish(stripMealSuffix(l.description))));
    const added: LineDraft[] = [];
    const orderSnapshot: ShipmentOrderBackedLineV1[] = [];
    const notProduced: string[] = [];
    for (const dish of dishes) {
      // FILTRO ANTI-DDT-FASULLO: includi SOLO i piatti con riscontro nella produzione
      // pianificata del giorno (`output_name` risolto con lo STESSO resolver → match
      // affidabile, porta lotto LP + trace_token/QR). Un piatto ordinato ma NON prodotto
      // NON entra nel documento: viene raccolto e segnalato all'operatore.
      const prod = dayProductions.find((p) =>
        normDish(p.output_name) === normDish(dish.recipe_name)
        && (!p.meal_service || p.meal_service === dish.meal_service));
      if (!prod) {
        notProduced.push(dish.recipe_name);
        continue;
      }
      const baseName = prod.output_name;
      orderSnapshot.push({
        production_id: prod.id,
        description: baseName,
        batch_lot: prod.lp_code ?? fallbackLot,
        qty: dish.qty,
        uom: 'pasti',
      });
      const key = normDish(baseName);
      if (existing.has(key)) continue;
      existing.add(key);
      added.push({
        production_id: prod.id,
        // Collettiva: il piatto finito non è a magazzino (parte subito) → nessun
        // food_id, l'emissione non genera stock. Traccia via production_id + lotto.
        food_id: null,
        description: baseName,
        batch_lot: prod.lp_code ?? fallbackLot,
        qty: String(dish.qty),
        uom: 'pasti',
        containers: '',
        notes: null,
      });
    }
    const excluded = [...new Set(notProduced)];
    setExcludedFromOrders(excluded);
    const mealLabel = ORDER_MEAL_LABEL[meal] ?? meal;
    if (added.length === 0) {
      if (excluded.length > 0) {
        setForm((current) => ({
          ...current,
          supply_allocations_v1: [],
        }));
        setToast(`Nessuno dei piatti ordinati per il ${mealLabel} risulta prodotto: pianifica/produci prima di emettere il DDT.`);
        return;
      }
      if (!shipmentLinesMatchOrderSnapshot(form.lines, orderSnapshot)) {
        setForm((current) => clearShipmentSupplyAllocations(current));
        setToast(
          `Le righe del ${mealLabel} sono state modificate: quantità, lotto, `
          + 'UdM e produzione devono coincidere con l’ordine. Ripristinale prima di collegare l’evasione.',
        );
        return;
      }
      setForm((current) => ({
        ...current,
        meal_service: meal,
        supply_allocations_v1: buildShipmentSupplyAllocations(
          destOrders.requests,
          meal,
        ),
      }));
      setToast(`Le righe del ${mealLabel} sono già tutte nel DDT · collegamento ordini aggiornato.`);
      return;
    }
    // Il servizio del documento = quello compilato (intestazione). Le
    // allocation vengono congelate solo se tutte le righe ordinate risultano
    // prodotte: un DDT parziale può essere emesso, ma non deve dichiarare
    // evaso l'intero servizio della richiesta.
    const prospectiveLines = [...form.lines, ...added];
    const exactOrderSnapshot = shipmentLinesMatchOrderSnapshot(
      prospectiveLines,
      orderSnapshot,
    );
    const allocations = excluded.length === 0 && exactOrderSnapshot
      ? buildShipmentSupplyAllocations(destOrders.requests, meal)
      : [];
    setForm((f) => ({
      ...f,
      meal_service: meal,
      supply_allocations_v1: allocations,
      lines: [...f.lines, ...added],
    }));
    setToast(
      exactOrderSnapshot
        ? `${added.length} piatti prodotti del ${mealLabel} inseriti${excluded.length ? ` · ${excluded.length} NON prodotti esclusi` : ''}`
        : `${added.length} piatti inseriti, ma una riga ordine già presente è stata modificata: evasione non collegata.`,
    );
  }

  function addFreeLine(): void {
    setForm((f) => ({
      ...clearShipmentSupplyAllocations(f),
      lines: [
        ...f.lines,
        {
          production_id: null,
          food_id: null,
          description: '',
          batch_lot: defaultMealLot(new Date(`${f.ref_date}T12:00:00`)),
          qty: '1',
          uom: 'pz',
          containers: '',
          notes: null,
        },
      ],
    }));
  }

  function addSelectedMeals(): void {
    const picked = selectableProductions.filter((p) => (
      mealSelected[p.id]
      && (collectiveFlow || !productionStockById.get(p.id)?.disabledReason)
    ));
    if (picked.length === 0) {
      if (!collectiveFlow && Object.values(mealSelected).some(Boolean)) {
        setToast('Le lavorazioni selezionate non hanno una quantità trasferibile verificabile');
      }
      setMealPickerOpen(false);
      return;
    }
    // Collettiva conserva il proprio fallback storico lotto-data. Nel flusso
    // ristorante il lotto è invece la coordinata stock canonica della
    // lavorazione: lp_code, oppure PROD-<production_id> per le manuali.
    setForm((f) => ({
      ...clearShipmentSupplyAllocations(f),
      lines: [
        ...f.lines,
        ...picked.map((p) => ({
          production_id: p.id,
          // T-221/T-425: aggancio allo stock del prodotto finito SOLO fuori
          // collettiva. In collettiva il piatto finito non è a magazzino (parte
          // subito) → food_id null, nessuno stock_out all'emissione. Traccia via
          // production_id + batch_lot.
          food_id: collectiveFlow ? null : (productionStockById.get(p.id)?.foodId ?? null),
          description: p.output_name,
          batch_lot: collectiveFlow
            ? (p.lp_code ?? defaultMealLot(new Date(p.produced_at)))
            : (productionStockById.get(p.id)?.lot ?? ''),
          // T-349 (review): le produzioni dal piano portano il TOTALE pianificato
          // (tutte le destinazioni). Pre-compilarlo su un DDT per UNA destinazione
          // = quantità sbagliata su documento di trasporto → qty vuota, va
          // digitata (il salvataggio la richiede > 0). Per "Compila dagli ordini"
          // la qty giusta arriva invece dall'ordine della destinazione.
          qty: (collectiveFlow && p.source_production_plan_item_id)
            ? ''
            : String(
                !collectiveFlow
                  && (productionStockById.get(p.id)?.availableQty ?? 0) > 0
                  ? productionStockById.get(p.id)!.availableQty
                  : (p.portions ?? p.output_qty ?? 1),
              ),
          uom: !collectiveFlow
            ? (productionStockById.get(p.id)?.uom ?? p.output_uom ?? 'pz')
            : (p.portions ? 'pasti' : (p.output_uom ?? 'pz')),
          containers: '',
          notes: null,
        })),
      ],
    }));
    setMealSelected({});
    setMealPickerOpen(false);
  }

  function buildLines(): { lines: ShipmentLine[] | null; error: string | null } {
    const out: ShipmentLine[] = [];
    for (const l of form.lines) {
      const qty = parseNum(l.qty);
      if (qty == null || qty <= 0) {
        return {
          lines: null,
          error: 'Completa le righe: la quantità deve essere maggiore di zero',
        };
      }

      if (!collectiveFlow) {
        if (!l.production_id) {
          return {
            lines: null,
            error: 'Ogni riga del DDT ristorante deve provenire da una lavorazione della sede',
          };
        }
        const production = eligibleRestaurantProductionById.get(l.production_id);
        const stock = productionStockById.get(l.production_id);
        if (!production || !stock) {
          return {
            lines: null,
            error: `${l.description || 'Riga'}: la lavorazione non appartiene più alla sede attiva`,
          };
        }
        if (stock.disabledReason || stock.availableQty == null || stock.availableQty <= 0) {
          return {
            lines: null,
            error: `${production.output_name}: ${stock.disabledReason ?? 'quantità non verificabile'}`,
          };
        }
        if (qty > stock.availableQty) {
          return {
            lines: null,
            error: warehouseEnabled
              ? `${production.output_name}: massimo disponibile ${stock.availableQty} ${stock.uom}`
              : `${production.output_name}: massimo dichiarato dalla lavorazione ${stock.availableQty} ${stock.uom}`,
          };
        }
        out.push({
          production_id: production.id,
          food_id: warehouseEnabled ? stock.foodId : null,
          stock_effect_v1: warehouseEnabled ? 'dispatch' : 'none',
          description: production.output_name,
          batch_lot: stock.lot,
          qty,
          uom: stock.uom,
          containers: l.containers.trim() || null,
          notes: l.notes,
        });
        continue;
      }

      const description = l.description.trim();
      const batchLot = l.batch_lot.trim();
      const uom = l.uom.trim();
      if (!description || !batchLot || !uom) {
        return {
          lines: null,
          error: 'Completa le righe: descrizione, lotto e unità sono obbligatori',
        };
      }
      const line: ShipmentLine = {
        production_id: l.production_id,
        food_id: l.food_id,
        description,
        batch_lot: batchLot,
        qty,
        uom,
        containers: l.containers.trim() || null,
        notes: l.notes,
      };
      const stockEffect = !collectiveFlow
        ? 'none'
        : warehouseEnabled
          ? shipmentLineStockEffectV1(line, true)
          : 'none';
      out.push({
        ...line,
        // New drafts are explicit. Saving an old collective finished-meal
        // draft also removes its obsolete food link so it cannot become a
        // reservation again on an older read path.
        food_id: stockEffect === 'none' ? null : line.food_id,
        stock_effect_v1: stockEffect,
      });
    }

    if (!collectiveFlow) {
      const productionTotals = new Map<string, number>();
      for (const line of out) {
        const productionId = line.production_id!;
        productionTotals.set(productionId, (productionTotals.get(productionId) ?? 0) + line.qty);
      }
      for (const [productionId, requestedQty] of productionTotals) {
        const production = eligibleRestaurantProductionById.get(productionId);
        const stock = productionStockById.get(productionId);
        if (!production || !stock?.declaredQty || requestedQty > stock.declaredQty + 1e-9) {
          return {
            lines: null,
            error: `${production?.output_name ?? 'Lavorazione'}: quantità complessiva superiore a quella prodotta`,
          };
        }
      }
      if (warehouseEnabled) {
        const availabilityByLocator = new Map<string, {
          food_id: string;
          batch_lot: string;
          available_qty: number;
          uom: string;
        }>();
        for (const line of out) {
          const stock = line.production_id
            ? productionStockById.get(line.production_id)
            : null;
          if (!stock) continue;
          if (!stock.foodId || stock.stockAvailableQty == null) continue;
          const key = `${stock.foodId}\u0000${stock.lot}`;
          availabilityByLocator.set(key, {
            food_id: stock.foodId,
            batch_lot: stock.lot,
            available_qty: stock.stockAvailableQty,
            uom: stock.uom,
          });
        }
        const aggregate = validateStockLocatorTotals(out, [...availabilityByLocator.values()]);
        if (!aggregate.valid) {
          return {
            lines: null,
            error: aggregate.error === 'insufficient_available_stock'
              ? `Lotto ${aggregate.batchLot ?? ''}: quantità complessiva ${aggregate.requestedQty ?? 0} superiore al saldo disponibile ${aggregate.availableQty ?? 0} ${aggregate.uom ?? ''}`
              : `Lotto ${aggregate.batchLot ?? ''}: saldo o unità di misura non verificabili`,
          };
        }
      }
    }
    return { lines: out, error: null };
  }

  function validateRestaurantDraftForIssue(shipment: Shipment): string | null {
    if (collectiveFlow) return null;
    const stockByProductionId = restaurantProductionStockFor(shipment, shipment.lines);
    const expectedStockEffect = warehouseEnabled ? 'dispatch' : 'none';
    const normalizedLines: ShipmentLine[] = [];
    const productionTotals = new Map<string, number>();

    for (const line of shipment.lines) {
      if (!line.production_id) {
        return 'Il DDT contiene una riga non collegata a una lavorazione. Riapri e risalva la bozza.';
      }
      const production = eligibleRestaurantProductionById.get(line.production_id);
      const stock = stockByProductionId.get(line.production_id);
      if (!production || !stock) {
        return 'Una lavorazione del DDT non appartiene più alla sede attiva. Riapri e risalva la bozza.';
      }
      const semanticsMatch = line.stock_effect_v1 === expectedStockEffect
        && (line.food_id ?? null) === (warehouseEnabled ? stock.foodId : null)
        && line.description === production.output_name
        && line.batch_lot.trim() === stock.lot
        && line.uom.trim().toLowerCase() === stock.uom.trim().toLowerCase();
      if (!semanticsMatch) {
        return 'La bozza non coincide con la configurazione magazzino o con i dati correnti della lavorazione. Riapri e risalva prima di emettere.';
      }
      if (stock.disabledReason || stock.availableQty == null || stock.availableQty <= 0) {
        return `${production.output_name}: ${stock.disabledReason ?? 'quantità non verificabile'}. Riapri e risalva la bozza.`;
      }
      const qty = Number(line.qty);
      if (!Number.isFinite(qty) || qty <= 0 || qty > stock.availableQty + 1e-9) {
        return `${production.output_name}: quantità non più compatibile con produzione e saldo correnti. Riapri e risalva la bozza.`;
      }
      productionTotals.set(
        production.id,
        (productionTotals.get(production.id) ?? 0) + qty,
      );
      normalizedLines.push(line);
    }

    for (const [productionId, requestedQty] of productionTotals) {
      const production = eligibleRestaurantProductionById.get(productionId);
      const declaredQty = stockByProductionId.get(productionId)?.declaredQty;
      if (!production || declaredQty == null || requestedQty > declaredQty + 1e-9) {
        return `${production?.output_name ?? 'Lavorazione'}: quantità complessiva superiore a quella prodotta. Riapri e risalva la bozza.`;
      }
    }
    if (warehouseEnabled) {
      const availabilityByLocator = new Map<string, {
        food_id: string;
        batch_lot: string;
        available_qty: number;
        uom: string;
      }>();
      for (const line of normalizedLines) {
        const stock = line.production_id
          ? stockByProductionId.get(line.production_id)
          : null;
        if (!stock) continue;
        if (!stock.foodId || stock.stockAvailableQty == null) continue;
        const key = `${stock.foodId}\u0000${stock.lot}`;
        availabilityByLocator.set(key, {
          food_id: stock.foodId,
          batch_lot: stock.lot,
          available_qty: stock.stockAvailableQty,
          uom: stock.uom,
        });
      }
      const aggregate = validateStockLocatorTotals(
        normalizedLines,
        [...availabilityByLocator.values()],
      );
      if (!aggregate.valid) {
        return `Il saldo del lotto ${aggregate.batchLot ?? ''} non copre più la quantità complessiva. Riapri e risalva la bozza.`;
      }
    }
    return null;
  }

  async function saveDraft(): Promise<void> {
    if (!profile?.active_tenant_id) {
      setToast('Nessuna azienda attiva');
      return;
    }
    if (!form.destination_id) {
      setToast('Seleziona la destinazione');
      return;
    }
    const built = buildLines();
    if (built.lines === null) {
      setToast(built.error ?? 'Le righe del documento non sono verificabili');
      return;
    }
    const lines = built.lines;
    const businessId = editing?.business_id ?? resolveBusinessId();
    if (!businessId) {
      setToast('Nessuna sede attiva: selezionala nelle impostazioni');
      return;
    }
    setSaving(true);
    try {
      const id = editing?.id ?? crypto.randomUUID();
      // OCC: base = version CORRENTE dal mirror locale. queries.ts scrive la
      // version ottimistica dentro la riga (insert → 0, update → base+1),
      // quindi rileggere da useMasters evita basi stantie se la riga è
      // cambiata (altro salvataggio/pull) dopo l'apertura del modale.
      const baseVersion = editing
        ? (shipments.find((x) => x.id === editing.id)?.version ?? editing.version ?? 0)
        : 0;
      const patch: Record<string, unknown> = {
        id,
        tenant_id: editing?.tenant_id ?? profile.active_tenant_id,
        business_id: businessId,
        kind: 'ddt',
        document_scope: collectiveFlow ? 'collective' : 'restaurant',
        status: 'draft',
        destination_id: form.destination_id,
        to_business_id: null,
        // T-447: servizio del DDT (intestazione) — le righe sono pulite.
        meal_service: collectiveFlow ? (form.meal_service.trim() || null) : null,
        service_date: form.ref_date,
        supply_allocations_v1: collectiveFlow ? form.supply_allocations_v1 : [],
        transport_cause: form.transport_cause.trim() || DEFAULT_TRANSPORT_CAUSE,
        invoice_reference: form.invoice_reference.trim() || null,
        carrier_name: form.carrier_name.trim() || null,
        vehicle_plate: form.vehicle_plate.trim() || null,
        temp_hot_c: collectiveFlow ? parseNum(form.temp_hot) : null,
        temp_cold_c: collectiveFlow ? parseNum(form.temp_cold) : null,
        vehicle_check_ok: collectiveFlow ? form.vehicle_check_ok : null,
        vehicle_check_notes: collectiveFlow ? (form.vehicle_check_notes.trim() || null) : null,
        lines,
        notes: form.notes.trim() || null,
      };
      await enqueueMaster.mutateAsync({
        table: 'shipments',
        id,
        op: editing ? 'update' : 'insert',
        base_version: baseVersion,
        patch,
      });
      setToast(editing ? 'Bozza aggiornata' : 'Bozza DDT salvata');
      setEditorOpen(false);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function issueDdt(s: Shipment): Promise<void> {
    if (!activeBusinessId || s.business_id !== activeBusinessId) {
      setToast(
        'Il DDT appartiene a un’altra sede: passa alla sede mittente prima di emetterlo.',
      );
      return;
    }
    if (!navigator.onLine) {
      setToast('Serve connessione per emettere il DDT: la bozza resta salvata.');
      return;
    }
    if (s.lines.length === 0) {
      setToast('Il DDT non ha righe: aggiungile prima di emetterlo');
      return;
    }
    const restaurantValidationError = validateRestaurantDraftForIssue(s);
    if (restaurantValidationError) {
      setToast(restaurantValidationError);
      return;
    }
    // Validazione PRIMA dell'emissione (review): righe e payload evento devono
    // essere conformi agli schemi ORA — scoprire un campo fuori misura DOPO la
    // numerazione farebbe perdere l'evento append-only shipment_dispatched.
    // Su abort la bozza resta intatta e nessun numero viene consumato.
    for (let i = 0; i < s.lines.length; i += 1) {
      const check = ShipmentLineSchema.safeParse(s.lines[i]);
      if (!check.success) {
        const field = check.error.issues[0]?.path.join('.') || 'campo';
        setToast(`Riga ${i + 1} non valida (${field}): correggi la bozza e riprova. Il DDT non è stato emesso.`);
        return;
      }
    }
    // T-425 (decisione owner 2026-07-07): in ristorazione collettiva i PIATTI
    // FINITI non vengono messi a magazzino — al momento della produzione sono già
    // in partenza verso le strutture dove verranno consumati. Quindi il DDT verso
    // una destinazione NON si gata sulla giacenza del prodotto finito (che sarebbe
    // sempre 0 → emissione sempre bloccata, era il bug "non ci sono piatti con quel
    // lotto"). La quantità spedita viene dall'ORDINE accettato, non dallo stock; la
    // tracciabilità resta perché ogni riga porta comunque il lotto (lp reale se il
    // piatto è stato etichettato, altrimenti lotto-data). Le etichette/lotti a
    // magazzino restano SOLO per i semilavorati intermedi (flusso "Preparazione"),
    // che sono consumati dopo e quindi vanno tracciati. I TRASFERIMENTI interni
    // (stock reale tra sedi) hanno una pagina separata (TransferPage) col suo gate.
    const payloadCheck = ShipmentDispatchedPayloadSchema.safeParse({
      shipment_id: s.id,
      kind: 'ddt',
      // Placeholder: il numero reale arriva dalla RPC; qui validiamo il resto.
      doc_number: 1,
      doc_year: new Date().getFullYear(),
      destination_id: s.destination_id,
      destination_name: s.destination_id ? (destById.get(s.destination_id)?.name ?? null) : null,
      line_count: s.lines.length,
      lines_summary: s.lines.map((l) => ({
        description: l.description,
        batch_lot: l.batch_lot,
        qty: l.qty,
        uom: l.uom,
      })),
      departure_at: new Date().toISOString(),
      temp_hot_c: s.temp_hot_c,
      temp_cold_c: s.temp_cold_c,
      vehicle_plate: s.vehicle_plate,
      carrier_name: s.carrier_name,
    });
    if (!payloadCheck.success) {
      const field = payloadCheck.error.issues[0]?.path.join('.') || 'documento';
      setToast(`Dati del documento non validi (${field}): correggi la bozza e riprova. Il DDT non è stato emesso.`);
      return;
    }
    const targetBusinessId = s.destination_id
      ? (destById.get(s.destination_id)?.business_id ?? null)
      : null;
    const linkedStockLines = s.lines.filter(
      (line): line is ShipmentLine & { food_id: string } => (
        !!line.food_id && shipmentLineReservesStockV1(line, collectiveFlow)
      ),
    );
    let stockCommand: StockCommandV1 | null = null;
    if (warehouseEnabled && linkedStockLines.length > 0) {
      try {
        const commandLines = linkedStockLines.map((line, index) => {
          const food = foods.find((candidate) => candidate.id === line.food_id);
          if (!food?.default_uom
            || food.default_uom.trim().toLowerCase() !== line.uom.trim().toLowerCase()) {
            throw new Error(
              `UdM non coerente per "${line.description}": il DDT usa ${line.uom}, `
              + `il prodotto usa ${food?.default_uom ?? 'UdM assente'}. Correggi prima di emettere.`,
            );
          }
          const origin = findExactThenLegacyStockEvent(
            stockIn as StockInRow[],
            s.business_id,
            allowLegacyUnscopedBusiness,
            (event) => (
              event.payload.food_id === line.food_id
              && (event.payload.batch_lot ?? '').trim() === line.batch_lot.trim()
              && event.payload.void_compensation !== true
            ),
          );
          const balance = stockProjection.balances.find((candidate) => (
            candidate.foodId === line.food_id
            && (candidate.batchLot ?? '') === (line.batch_lot?.trim() ?? '')
          ));
          const carryingCost = balance
            ? projectedCarryingCost(balance)
            : { unitCost: null, currency: 'EUR' };
          return {
            food_id: line.food_id,
            batch_lot: line.batch_lot,
            expiration_date: origin?.payload.expiration_date ?? null,
            supplier_id: origin?.payload.supplier_id ?? null,
            document_id: origin?.payload.document_id ?? null,
            storage_unit_id: null,
            line_ref: `shipment:${s.id}:${index + 1}`,
            lot_action: 'recorded_only' as const,
            source_qty: line.qty,
            source_uom: line.uom,
            source_unit_price: carryingCost.unitCost,
            source_line_total: carryingCost.unitCost == null
              ? null
              : carryingCost.unitCost * line.qty,
            currency: carryingCost.currency,
            stock_uom: line.uom,
            conversion: { factor: 1, source: 'identity' as const },
          };
        });
        stockCommand = StockCommandV1Schema.parse(targetBusinessId
          ? {
              version: 1,
              command_id: s.id,
              tenant_id: s.tenant_id,
              movement_class: 'transfer',
              economic_effect: 'internal',
              reason: 'shipment_dispatch_internal',
              from_business_id: s.business_id,
              to_business_id: targetBusinessId,
              shipment_id: s.id,
              lines: commandLines,
            }
          : {
              version: 1,
              command_id: s.id,
              tenant_id: s.tenant_id,
              movement_class: 'dispatch',
              economic_effect: 'stock_only',
              reason: 'shipment_dispatch_external',
              business_id: s.business_id,
              shipment_id: s.id,
              lines: commandLines,
            });
      } catch (error) {
        setToast(`${toUserMessage(error)} Il DDT non è stato emesso.`);
        return;
      }
    }
    setIssuingId(s.id);
    try {
      // Conferma esattamente QUESTA bozza prima della numerazione. Un tick
      // globale può terminare senza aver raggiunto la riga; un conflitto OCC
      // adotta la copia server ma non autorizza l'emissione di contenuto stantio.
      const confirmation = await getSyncEngine().confirmMasterPushed(
        'shipments',
        s.id,
        {
          timeoutMs: 12_000,
          acceptConflict: false,
          expectedPatch: s as unknown as Record<string, unknown>,
        },
      );
      if (!confirmation.confirmed) {
        const conflictHint = confirmation.reason === 'master_version_conflict'
          ? ' La bozza è stata modificata altrove: aggiorna la pagina.'
          : '';
        setToast(`Sincronizzazione non riuscita: il DDT non è stato emesso.${conflictHint} La bozza resta salvata.`);
        return;
      }
      const finalRestaurantValidationError = validateRestaurantDraftForIssue(s);
      if (finalRestaurantValidationError) {
        setToast(finalRestaurantValidationError);
        return;
      }
      let res: { doc_number: number; doc_year: number };
      if (stockCommand) {
        res = await getSyncEngine().issueShipmentWithStock(s.id, stockCommand);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['events'] }),
          queryClient.invalidateQueries({ queryKey: ['masters'] }),
        ]);
      } else {
        // Anche un DDT solo documentale passa dal wrapper atomico: numero,
        // shipment_dispatched e allocation ordini devono riuscire insieme.
        res = await getSyncEngine().issueShipmentWithoutStock(s.id);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['events'] }),
          queryClient.invalidateQueries({ queryKey: ['masters'] }),
        ]);
      }
      // Per il path stock-backed master issued, stock e shipment_dispatched
      // sono già nel writeback atomico applicato dal SyncEngine.
      setIssuedLocal((m) => ({ ...m, [s.id]: { doc_number: res.doc_number, doc_year: res.doc_year } }));
      setSegment('emessi');
      setPdfPrompt(s);
      setToast(`DDT ${res.doc_number}/${res.doc_year} emesso`);
      const skipped = s.lines.length - linkedStockLines.length;
      if (warehouseEnabled && skipped > 0) {
        setToast(
          `DDT ${res.doc_number}/${res.doc_year} emesso · ${skipped} `
          + `${skipped === 1 ? 'riga documentale' : 'righe documentali'}: `
          + 'nessun movimento di magazzino per quelle.',
        );
      }
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setIssuingId(null);
    }
  }

  async function openPdf(s: Shipment): Promise<void> {
    if (!navigator.onLine) {
      setToast('Serve connessione per generare il PDF del documento.');
      return;
    }
    setPdfBusyId(s.id);
    try {
      const { data } = await getSupabase().auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Non autenticato');
      const res = await fetch(`${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/export-ddt`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ shipment_id: s.id, dish_recipes: buildDishRecipes(s) }),
      });
      if (!res.ok) {
        let detail = '';
        try {
          const j = (await res.clone().json()) as { detail?: { message?: string }; error?: string };
          detail = j?.detail?.message ?? j?.error ?? '';
        } catch {
          detail = await res.text().catch(() => '');
        }
        throw new Error(`HTTP ${res.status}${detail ? ` — ${detail}` : ''}`);
      }
      const contentType = res.headers.get('content-type') ?? '';
      let blob: Blob;
      if (contentType.includes('application/pdf')) {
        blob = await res.blob();
      } else {
        const j = (await res.json()) as { pdf_url?: string };
        if (!j.pdf_url) throw new Error('pdf_url mancante nella risposta');
        const pdfRes = await fetch(j.pdf_url);
        if (!pdfRes.ok) throw new Error(`Download PDF fallito (HTTP ${pdfRes.status})`);
        blob = await pdfRes.blob();
      }
      const n = effNumber(s);
      const y = effYear(s);
      const fileName = `trackeat-ddt-${y ?? 'bozza'}-${n ?? s.id.slice(0, 8)}.pdf`;
      if (Capacitor.isNativePlatform()) {
        // Nella WebView Capacitor <a download> non funziona: scriviamo in Cache
        // e apriamo lo Share sheet nativo (salva/condividi/apri con PDF viewer).
        const base64 = await blobToBase64(blob);
        const written = await Filesystem.writeFile({
          path: fileName,
          data: base64,
          directory: Directory.Cache,
          recursive: true,
        });
        await Share.share({
          title: `Documento di trasporto ${formatShipmentNumber(n, y, 'ddt')}`,
          url: written.uri,
          dialogTitle: 'Salva o condividi il DDT',
        });
        setToast('PDF pronto — scegli dove salvarlo');
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
        setToast('PDF scaricato');
      }
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setPdfBusyId(null);
    }
  }

  async function voidDdt(s: Shipment, reason: string): Promise<void> {
    if (voidBusy.current) return; // fix #5: già in corso → no doppia compensazione
    voidBusy.current = true;
    try {
      const motive = reason.trim() || 'non specificato';
      if (!navigator.onLine) {
        throw new Error('Serve connessione per annullare un DDT emesso');
      }
      const engine = getSyncEngine();
      let partialStockRestore = false;
      try {
        const preparation = await engine.prepareShipmentStockVoid(s.id);
        partialStockRestore = preparation.movements.some(
          (movement) => movement.max_reversible_qty < movement.stock_qty,
        );
        const reverseCommand = buildShipmentStockVoidCommand(
          preparation,
          await shipmentStockVoidCommandIdFor(s.tenant_id, s.id),
          motive,
        );
        await engine.voidShipmentWithStock(s.id, motive, reverseCommand);
      } catch (prepareError) {
        const message = prepareError instanceof Error
          ? prepareError.message
          : String(prepareError);
        if (!message.includes('shipment_stock_void_prepare_v1_receipt_missing')) {
          throw prepareError;
        }
        // DDT solo documentale / magazzino disabilitato. La RPC verifica
        // server-side che non esistano né receipt v1 né vecchi eventi stock:
        // un documento con movimenti non può quindi essere annullato senza
        // la compensazione corrispondente.
        await engine.voidShipmentWithoutStock(s.id, motive);
      }
      setVoidedLocal((current) => new Set(current).add(s.id));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['events'] }),
        queryClient.invalidateQueries({ queryKey: ['masters'] }),
      ]);
      // T-435 (b): libera il PROGRESSIVO se questo DDT aveva l'ultimo numero
      // (il prossimo DDT lo riusa). Server-side via RPC: serve che il void sia già
      // sincronizzato, quindi si drena prima la coda. Best-effort: se fallisce, il
      // numero resta consumato (comportamento fiscale standard) ma il DDT è annullato.
      try {
        await drainSyncQueue();
        await getSupabase().rpc('release_ddt_number', { p_shipment_id: s.id });
      } catch { /* non-fatale */ }
      setToast(
        `${formatShipmentNumber(effNumber(s), effYear(s), 'ddt')} annullato`
        + (partialStockRestore
          ? ' — ripristinata solo la merce ancora presente a destinazione.'
          : ''),
      );
      setVoidTarget(null);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      voidBusy.current = false;
    }
  }

  async function deleteDraft(s: Shipment): Promise<void> {
    try {
      // Version corrente dal mirror (dopo un insert è 0, dopo un update base+1).
      const current = shipments.find((x) => x.id === s.id) ?? s;
      await enqueueMaster.mutateAsync({
        table: 'shipments',
        id: s.id,
        op: 'archive',
        base_version: current.version ?? 0,
        patch: { id: s.id },
      });
      setToast('Bozza eliminata');
      setDeleteTarget(null);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  function destName(s: Shipment): string {
    if (!s.destination_id) return 'Destinazione non indicata';
    return destById.get(s.destination_id)?.name ?? 'Destinazione non trovata';
  }

  function rowDate(s: Shipment): string {
    const iso = s.issued_at ?? s.created_at;
    return iso ? new Date(iso).toLocaleDateString('it-IT') : '—';
  }

  if (!collectiveFlow && transportAccess.checking) {
    return (
      <IonPage>
        <IonContent className="ion-padding">
          <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
            <IonSpinner name="crescent" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const moduleEnabled = collectiveFlow ? collettivaEnabled : transportAccess.enabled;
  if (!moduleEnabled) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref={collectiveFlow ? '/today' : '/documents/transport'} />
            </IonButtons>
            <IonTitle>{collectiveFlow ? 'Spedizioni / DDT' : 'DDT di vendita'}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: 14, fontSize: 13, color: '#9a3412' }}>
            {collectiveFlow
              ? 'Modulo collettiva non attivo. Attivalo dalle Impostazioni per gestire DDT e spedizioni verso scuole, ospedali e altre destinazioni.'
              : transportAccess.error
                ? 'Impossibile verificare in modo sicuro l’accesso al modulo DDT. Riprova con connessione disponibile.'
                : 'Modulo Documenti di trasporto non attivo per questo locale.'}
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const listed = segment === 'bozze' ? drafts : issued;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={collectiveFlow ? '/today' : '/documents/transport'} />
          </IonButtons>
          <IonTitle>{collectiveFlow ? 'Spedizioni / DDT' : 'DDT di vendita'}</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={segment} onIonChange={(ev) => setSegment((ev.detail.value as 'bozze' | 'emessi') ?? 'bozze')}>
            <IonSegmentButton value="bozze">
              <IonLabel>Bozze ({drafts.length})</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="emessi">
              <IonLabel>Emessi ({issued.length})</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        <div style={{ padding: '12px 16px 96px' }}>
          {!collectiveFlow && (
            <div style={{
              background: warehouseEnabled ? '#ecfdf5' : '#eff6ff',
              border: `1px solid ${warehouseEnabled ? '#a7f3d0' : '#bfdbfe'}`,
              borderRadius: 12,
              padding: '10px 14px',
              fontSize: 12.5,
              color: warehouseEnabled ? '#065f46' : '#1e40af',
              marginBottom: 12,
            }}>
              {warehouseEnabled
                ? 'Magazzino attivo: il DDT scarica i lotti selezionati e le bozze ne riservano la quantità.'
                : 'Magazzino non attivo: il DDT conserva prodotto, lotto e quantità dichiarata senza simulare una giacenza.'}
            </div>
          )}
          {listed.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 14, padding: 16, color: '#64748b', fontStyle: 'italic', fontSize: 13 }}>
              {segment === 'bozze'
                ? 'Nessuna bozza. Tocca + per creare un nuovo DDT.'
                : 'Nessun documento emesso.'}
            </div>
          ) : (
            listed.map((s) => {
              const status = effStatus(s);
              const isDraft = status === 'draft';
              return (
                <div
                  key={s.id}
                  onClick={() => openEdit(s)}
                  role={isDraft ? 'button' : undefined}
                  style={{ ...cardStyle, cursor: isDraft ? 'pointer' : 'default' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                      {isDraft ? 'DDT' : formatShipmentNumber(effNumber(s), effYear(s), 'ddt')}
                    </span>
                    <span style={statusBadgeStyle(status)}>{STATUS_BADGE[status].label}</span>
                  </div>
                  <div style={{ fontSize: 14, color: '#334155', marginTop: 4 }}>{destName(s)}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {rowDate(s)} · {s.lines.length} {s.lines.length === 1 ? 'riga' : 'righe'}
                    {s.invoice_reference ? ` · Fattura ${s.invoice_reference}` : ''}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                    {isDraft ? (
                      <>
                        <IonButton size="small" disabled={issuingId === s.id} onClick={() => { void issueDdt(s); }}>
                          {issuingId === s.id ? <IonSpinner name="dots" /> : (
                            <>
                              <IonIcon icon={paperPlaneOutline} slot="start" />
                              Emetti DDT
                            </>
                          )}
                        </IonButton>
                        <IonButton size="small" fill="outline" color="danger" onClick={() => setDeleteTarget(s)}>
                          <IonIcon icon={trashOutline} slot="start" />
                          Elimina bozza
                        </IonButton>
                      </>
                    ) : (
                      <>
                        <IonButton size="small" fill="outline" disabled={pdfBusyId === s.id} onClick={() => { void openPdf(s); }}>
                          {pdfBusyId === s.id ? <IonSpinner name="dots" /> : (
                            <>
                              <IonIcon icon={documentTextOutline} slot="start" />
                              PDF
                            </>
                          )}
                        </IonButton>
                        {status === 'issued' && (
                          <IonButton size="small" fill="outline" color="danger" onClick={() => setVoidTarget(s)}>
                            <IonIcon icon={closeOutline} slot="start" />
                            Annulla DDT
                          </IonButton>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <IonFab slot="fixed" vertical="bottom" horizontal="end">
          <IonFabButton onClick={() => openNew()} aria-label="Nuovo DDT">
            <IonIcon icon={add} />
          </IonFabButton>
        </IonFab>

        {/* ───────────── Editor bozza DDT ───────────── */}
        <IonModal isOpen={editorOpen} onDidDismiss={() => setEditorOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{editing ? 'Modifica bozza DDT' : 'Nuovo DDT'}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setEditorOpen(false)} aria-label="chiudi">
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding" style={{ '--background': '#eef2f7' } as React.CSSProperties}>
            <h2 style={{ ...sectionHeaderStyle, marginTop: 4 }}>1 · Destinazione e data</h2>
            <div style={stripedCardStyle}>
              <div style={stripeStyle(!!form.destination_id)} />
              <div style={{ flex: 1, minWidth: 0, padding: '2px 4px' }}>
                <IonItem lines={activeDestinations.length === 0 ? 'none' : 'full'} style={{ '--background': 'transparent' } as React.CSSProperties}>
                  <IonSelect
                    label={collectiveFlow ? 'Destinazione *' : 'Cliente / destinatario *'}
                    labelPlacement="stacked"
                    placeholder={collectiveFlow ? 'Scegli scuola/ospedale/…' : 'Scegli il cliente'}
                    value={form.destination_id || undefined}
                    onIonChange={(ev) => setForm((f) => ({
                      ...clearShipmentSupplyAllocations(f),
                      destination_id: String(ev.detail.value ?? ''),
                    }))}
                  >
                    {activeDestinations.map((d) => (
                      <IonSelectOption key={d.id} value={d.id}>{d.name}</IonSelectOption>
                    ))}
                  </IonSelect>
                </IonItem>
                {activeDestinations.length === 0 && (
                  <IonItem lines="full" style={{ '--background': 'transparent' } as React.CSSProperties}>
                    <IonText color="medium">
                      <p style={{ fontSize: 12, margin: 0 }}>
                        {collectiveFlow
                          ? 'Nessuna destinazione in anagrafica: creala da Impostazioni → Destinazioni.'
                          : 'Nessun cliente in anagrafica. Crealo dalla sezione Clienti e destinatari.'}
                      </p>
                    </IonText>
                  </IonItem>
                )}
                <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                  <IonInput
                    type="date"
                    label="Data di riferimento"
                    labelPlacement="stacked"
                    value={form.ref_date}
                    onIonInput={(ev) => setForm((f) => ({
                      ...clearShipmentSupplyAllocations(f),
                      ref_date: String(ev.detail.value ?? '') || f.ref_date,
                    }))}
                  />
                </IonItem>
              </div>
            </div>

            {/* T-349 Fase 2 / T-447: compila il DDT dagli ordini accettati, UN
                servizio per volta (pranzo/cena sono spedizioni distinte). */}
            {collectiveFlow && form.destination_id && (
              <div style={{ margin: '10px 0 2px' }}>
                {availableMeals.length > 1 && (
                  <IonSegment
                    value={effPrefillMeal}
                    onIonChange={(ev) => {
                      const v = String(ev.detail.value ?? 'pranzo');
                      setPrefillMeal(v);
                      setForm((f) => ({
                        ...clearShipmentSupplyAllocations(f),
                        meal_service: v,
                      }));
                    }}
                    style={{ marginBottom: 8 }}
                  >
                    {availableMeals.map((mm) => (
                      <IonSegmentButton key={mm} value={mm}>
                        <IonLabel>{ORDER_MEAL_LABEL[mm] ?? mm} ({ordersByMeal.get(mm)?.pasti ?? 0})</IonLabel>
                      </IonSegmentButton>
                    ))}
                  </IonSegment>
                )}
                {form.meal_service && (
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0369a1', margin: '0 4px 6px' }}>
                    🍽 Servizio del documento: {ORDER_MEAL_LABEL[form.meal_service] ?? form.meal_service} — scritto in intestazione, non su ogni riga
                  </div>
                )}
                <IonButton
                  expand="block"
                  fill={availableMeals.length > 0 ? 'solid' : 'outline'}
                  disabled={availableMeals.length === 0}
                  onClick={prefillFromOrders}
                >
                  🧾 Compila il {ORDER_MEAL_LABEL[effPrefillMeal] ?? effPrefillMeal} dagli ordini
                  {ordersByMeal.get(effPrefillMeal)
                    ? ` (${ordersByMeal.get(effPrefillMeal)!.pasti} pasti)`
                    : ''}
                </IonButton>
                {availableMeals.length === 0 && (
                  <p style={{ fontSize: 12, color: '#64748b', margin: '6px 4px 0' }}>
                    Nessun ordine accettato per questa destinazione il {form.ref_date.split('-').reverse().join('/')}.
                    Le richieste si accettano in &quot;Richieste fornitura&quot;.
                  </p>
                )}
                {availableMeals.length > 1 && (
                  <p style={{ fontSize: 12, color: '#64748b', margin: '6px 4px 0' }}>
                    Pranzo e cena sono spedizioni separate: compila e emetti un DDT per servizio.
                  </p>
                )}
                {excludedFromOrders.length > 0 && (
                  <div style={{ marginTop: 8, padding: '8px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c' }}>
                      ⚠️ {excludedFromOrders.length} {excludedFromOrders.length === 1 ? 'piatto ordinato NON risulta prodotto' : 'piatti ordinati NON risultano prodotti'} — esclusi dal DDT
                    </div>
                    <div style={{ fontSize: 12, color: '#7f1d1d', marginTop: 2 }}>{excludedFromOrders.join(' · ')}</div>
                    <div style={{ fontSize: 11, color: '#991b1b', marginTop: 4 }}>
                      Il DDT contiene solo ciò che è stato prodotto. Se vanno spediti, pianificali/producili in Produzione, poi ricompila.
                    </div>
                  </div>
                )}
              </div>
            )}

            <h2 style={sectionHeaderStyle}>
              2 · {collectiveFlow ? 'Pasti' : 'Prodotti e lotti'} / righe ({form.lines.length})
            </h2>
            {form.lines.length === 0 && (
              <div style={stripedCardStyle}>
                <div style={stripeStyle(false)} />
                <div style={{ flex: 1, padding: '14px', color: '#64748b', fontSize: 13, fontStyle: 'italic' }}>
                  {collectiveFlow
                    ? 'Nessuna riga: aggiungi i pasti del giorno o una riga libera.'
                    : 'Nessuna riga: richiama una lavorazione prodotta oggi o nello storico.'}
                </div>
              </div>
            )}
            {form.lines.map((l, idx) => {
              const qtyNum = parseNum(l.qty);
              const productionBound = !collectiveFlow && !!l.production_id;
              const lineComplete =
                !!l.description.trim() && !!l.batch_lot.trim() && !!l.uom.trim() && qtyNum != null && qtyNum > 0;
              return (
                <div key={`${l.production_id ?? 'free'}-${idx}`} style={stripedCardStyle}>
                  <div style={stripeStyle(lineComplete)} />
                  <div style={{ flex: 1, minWidth: 0, padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ flex: 1 }}>
                        <IonInput
                          label="Descrizione *"
                          labelPlacement="stacked"
                          maxlength={300}
                          value={l.description}
                          placeholder="Es. Pasta al forno"
                          readonly={productionBound}
                          onIonInput={productionBound
                            ? undefined
                            : (ev) => updateLine(idx, { description: String(ev.detail.value ?? '') })}
                        />
                      </div>
                      <IonButton fill="clear" size="small" color="danger" aria-label="Rimuovi riga" onClick={() => removeLine(idx)}>
                        <IonIcon icon={trashOutline} slot="icon-only" />
                      </IonButton>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <IonInput
                          label="Lotto *"
                          labelPlacement="stacked"
                          maxlength={100}
                          value={l.batch_lot}
                          readonly={productionBound}
                          onIonInput={productionBound
                            ? undefined
                            : (ev) => updateLine(idx, { batch_lot: String(ev.detail.value ?? '') })}
                        />
                      </div>
                      <div style={{ width: 90 }}>
                        <IonInput
                          label="Qtà *"
                          labelPlacement="stacked"
                          inputmode="decimal"
                          value={l.qty}
                          onIonInput={(ev) => updateLine(idx, { qty: String(ev.detail.value ?? '') })}
                        />
                      </div>
                      <div style={{ width: 90 }}>
                        <IonInput
                          label="UdM *"
                          labelPlacement="stacked"
                          maxlength={20}
                          value={l.uom}
                          readonly={productionBound}
                          onIonInput={productionBound
                            ? undefined
                            : (ev) => updateLine(idx, { uom: String(ev.detail.value ?? '') })}
                        />
                      </div>
                    </div>
                    <IonInput
                      label="Contenitori"
                      labelPlacement="stacked"
                      maxlength={100}
                      value={l.containers}
                      placeholder="Es. 2 GN isotermici"
                      onIonInput={(ev) => updateLine(idx, { containers: String(ev.detail.value ?? '') })}
                    />
                    {l.production_id && (
                      <div style={{ fontSize: 11, color: '#0369a1', marginTop: 6 }}>
                        {productionBound
                          ? 'Descrizione, lotto e UdM derivano dalla lavorazione; puoi modificare solo la quantità.'
                          : 'Collegata alla lavorazione prodotta (tracciabilità automatica)'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <IonButton size="small" fill="outline" onClick={() => {
                setMealSelected({});
                setProductionScope('today');
                setProductionSearch('');
                setMealPickerOpen(true);
              }}>
                <IonIcon icon={restaurantOutline} slot="start" />
                {collectiveFlow ? 'Aggiungi dai pasti del giorno' : 'Richiama lavorazioni e lotti'}
              </IonButton>
              {collectiveFlow && (
                <IonButton size="small" fill="outline" onClick={() => addFreeLine()}>
                  <IonIcon icon={addCircleOutline} slot="start" />
                  Riga libera
                </IonButton>
              )}
            </div>

            <h2 style={sectionHeaderStyle}>3 · Trasporto (facoltativo)</h2>
            <div style={{ ...cardStyle, padding: '2px 4px' }}>
              <IonItem style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonInput
                  label="Causale"
                  labelPlacement="stacked"
                  maxlength={200}
                  value={form.transport_cause}
                  onIonInput={(ev) => setForm((f) => ({ ...f, transport_cause: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              {!collectiveFlow && (
                <IonItem style={{ '--background': 'transparent' } as React.CSSProperties}>
                  <IonInput
                    label="Riferimento fattura (facoltativo)"
                    labelPlacement="stacked"
                    maxlength={100}
                    placeholder="Es. FT 24/2026"
                    value={form.invoice_reference}
                    onIonInput={(ev) => setForm((f) => ({
                      ...f,
                      invoice_reference: String(ev.detail.value ?? ''),
                    }))}
                  />
                </IonItem>
              )}
              <IonItem style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonInput
                  label="Vettore"
                  labelPlacement="stacked"
                  maxlength={200}
                  placeholder="Vuoto = trasporto in proprio"
                  value={form.carrier_name}
                  onIonInput={(ev) => setForm((f) => ({ ...f, carrier_name: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonInput
                  label="Targa automezzo"
                  labelPlacement="stacked"
                  autocapitalize="characters"
                  maxlength={20}
                  value={form.vehicle_plate}
                  onIonInput={(ev) => setForm((f) => ({ ...f, vehicle_plate: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              {collectiveFlow && (
                <>
                  <IonItem style={{ '--background': 'transparent' } as React.CSSProperties}>
                    <IonInput
                      label="Temp. pasti caldi (°C)"
                      labelPlacement="stacked"
                      inputmode="decimal"
                      placeholder="≥ 60-65 °C"
                      value={form.temp_hot}
                      onIonInput={(ev) => setForm((f) => ({ ...f, temp_hot: String(ev.detail.value ?? '') }))}
                    />
                  </IonItem>
                  <IonItem style={{ '--background': 'transparent' } as React.CSSProperties}>
                    <IonInput
                      label="Temp. pasti freddi (°C)"
                      labelPlacement="stacked"
                      inputmode="decimal"
                      placeholder="≤ 10 °C"
                      value={form.temp_cold}
                      onIonInput={(ev) => setForm((f) => ({ ...f, temp_cold: String(ev.detail.value ?? '') }))}
                    />
                  </IonItem>
                  <IonItem style={{ '--background': 'transparent' } as React.CSSProperties}>
                    <IonToggle
                      checked={form.vehicle_check_ok}
                      onIonChange={(ev) => setForm((f) => ({ ...f, vehicle_check_ok: !!ev.detail.checked }))}
                    >
                      Ispezione vano di carico OK
                    </IonToggle>
                  </IonItem>
                  <IonItem style={{ '--background': 'transparent' } as React.CSSProperties}>
                    <IonInput
                      label="Note ispezione vano"
                      labelPlacement="stacked"
                      value={form.vehicle_check_notes}
                      onIonInput={(ev) => setForm((f) => ({ ...f, vehicle_check_notes: String(ev.detail.value ?? '') }))}
                    />
                  </IonItem>
                </>
              )}
              <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel position="stacked">Note documento</IonLabel>
                <IonTextarea
                  autoGrow
                  rows={2}
                  value={form.notes}
                  onIonInput={(ev) => setForm((f) => ({ ...f, notes: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
            </div>

            <IonButton
              expand="block"
              disabled={saving}
              onClick={() => { void saveDraft(); }}
              style={{ marginTop: 20, height: 52, '--border-radius': '14px', fontWeight: 700 } as React.CSSProperties}
            >
              {saving ? <IonSpinner name="dots" /> : 'SALVA BOZZA'}
            </IonButton>
            <IonText color="medium">
              <p style={{ fontSize: 12, textAlign: 'center' }}>
                La bozza si salva anche offline. L'emissione (numero ufficiale) richiede connessione.
              </p>
            </IonText>
          </IonContent>
        </IonModal>

        {/* ───────────── Picker produzioni / lotti ───────────── */}
        <IonModal isOpen={mealPickerOpen} onDidDismiss={() => setMealPickerOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>
                {collectiveFlow
                  ? `Pasti del ${new Date(`${form.ref_date}T12:00:00`).toLocaleDateString('it-IT')}`
                  : 'Lavorazioni e lotti'}
              </IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setMealPickerOpen(false)} aria-label="chiudi">
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {!collectiveFlow && (
              <>
                <IonSegment
                  value={productionScope}
                  onIonChange={(ev) => {
                    setProductionScope((ev.detail.value as 'today' | 'history') ?? 'today');
                    setMealSelected({});
                  }}
                >
                  <IonSegmentButton value="today">
                    <IonLabel>Oggi ({restaurantCandidates.today.length})</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="history">
                    <IonLabel>Storico ({restaurantCandidates.history.length})</IonLabel>
                  </IonSegmentButton>
                </IonSegment>
                <IonSearchbar
                  placeholder="Cerca prodotto o lotto"
                  value={productionSearch}
                  debounce={150}
                  onIonInput={(ev) => {
                    setProductionSearch(String(ev.detail.value ?? ''));
                    setMealSelected({});
                  }}
                  style={{ paddingInline: 0 }}
                />
              </>
            )}
            {selectableProductions.length === 0 ? (
              <IonText color="medium">
                <p style={{ fontSize: 13 }}>
                  {collectiveFlow && form.ref_date >= COLLETTIVA_PLAN_ONLY_FROM
                    ? 'Nessuna produzione del piano per questo giorno (o già tutte nel documento). I pasti spedibili nascono da "Pianifica produzione"; per le quantità della destinazione usa "Compila dagli ordini accettati".'
                    : !collectiveFlow && productionSearch
                      ? 'Nessuna lavorazione corrisponde alla ricerca.'
                      : !collectiveFlow && productionScope === 'history'
                        ? 'Nessuna lavorazione storica disponibile.'
                        : 'Nessuna produzione registrata per questo giorno (o già tutte aggiunte al documento).'}
                </p>
              </IonText>
            ) : (
              <IonList>
                {selectableProductions.map((p) => {
                  const stock = productionStockById.get(p.id);
                  const unavailable = !collectiveFlow && !!stock?.disabledReason;
                  return (
                  <IonItem key={p.id} disabled={unavailable}>
                    <IonCheckbox
                      checked={!!mealSelected[p.id]}
                      disabled={unavailable}
                      onIonChange={(ev) => setMealSelected((m) => ({ ...m, [p.id]: !!ev.detail.checked }))}
                    >
                      <IonLabel>
                        <h3>{p.output_name}</h3>
                        <p>
                          {p.portions ? `${p.portions} pasti` : `${p.output_qty ?? 1} ${p.output_uom ?? 'pz'}`}
                          {p.meal_service ? ` · ${ORDER_MEAL_LABEL[p.meal_service] ?? p.meal_service}` : ''}
                          {!collectiveFlow ? ` · ${new Date(p.produced_at).toLocaleDateString('it-IT')}` : ''}
                        </p>
                        {!collectiveFlow && (
                          <p>
                            Lotto {stock?.lot ?? canonicalProductionLot({
                              id: p.id,
                              lp_code: p.lp_code ?? null,
                            })}
                            {stock?.disabledReason
                              ? ` · non selezionabile: ${stock.disabledReason}`
                              : warehouseEnabled
                                ? ` · disponibili ${stock?.availableQty ?? 0} ${stock?.uom ?? ''}`
                                : ` · quantità dichiarata ${stock?.availableQty ?? 0} ${stock?.uom ?? ''}, magazzino non attivo`}
                        </p>
                        )}
                      </IonLabel>
                    </IonCheckbox>
                  </IonItem>
                  );
                })}
              </IonList>
            )}
            <IonButton
              expand="block"
              style={{ marginTop: 16 }}
              disabled={selectableProductions.every((p) => (
                !mealSelected[p.id]
                || (!collectiveFlow && !!productionStockById.get(p.id)?.disabledReason)
              ))}
              onClick={() => addSelectedMeals()}
            >
              Aggiungi {selectableProductions.filter((p) => mealSelected[p.id]).length || ''} righe
            </IonButton>
          </IonContent>
        </IonModal>

        {/* Conferma emissione → proposta apertura PDF */}
        <IonAlert
          isOpen={!!pdfPrompt}
          header="DDT emesso"
          message={pdfPrompt ? `${formatShipmentNumber(effNumber(pdfPrompt), effYear(pdfPrompt), 'ddt')} emesso. Vuoi aprire il PDF da consegnare al vettore?` : ''}
          buttons={[
            { text: 'Più tardi', role: 'cancel' },
            { text: 'Apri PDF', handler: () => { if (pdfPrompt) void openPdf(pdfPrompt); } },
          ]}
          onDidDismiss={() => setPdfPrompt(null)}
        />

        {/* Annulla documento emesso (mai delete: status void + motivo) */}
        <IonAlert
          isOpen={!!voidTarget}
          header="Annulla DDT"
          message={voidTarget ? `Annullare ${formatShipmentNumber(effNumber(voidTarget), effYear(voidTarget), 'ddt')}? Il documento resta in archivio come ANNULLATO.` : ''}
          inputs={[{ name: 'reason', type: 'textarea', placeholder: 'Motivo dell\'annullamento *' }]}
          buttons={[
            { text: 'Indietro', role: 'cancel' },
            {
              text: 'Annulla DDT',
              role: 'destructive',
              handler: (vals: { reason?: string }) => {
                if (voidTarget) void voidDdt(voidTarget, String(vals?.reason ?? ''));
              },
            },
          ]}
          onDidDismiss={() => setVoidTarget(null)}
        />

        {/* Elimina bozza (archiviazione, mai delete fisico) */}
        <IonAlert
          isOpen={!!deleteTarget}
          header="Elimina bozza"
          message={deleteTarget ? `Eliminare la bozza per "${destName(deleteTarget)}"?` : ''}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            { text: 'Elimina', role: 'destructive', handler: () => { if (deleteTarget) void deleteDraft(deleteTarget); } },
          ]}
          onDidDismiss={() => setDeleteTarget(null)}
        />

        <IonToast isOpen={!!toast} message={toast ?? ''} duration={2500} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}

export function ShipmentsPage(): JSX.Element {
  return <ShipmentsPageContent flow="collective" />;
}

export function RestaurantShipmentsPage(): JSX.Element {
  return <ShipmentsPageContent flow="restaurant" />;
}
